import { chatService } from '@/lib/services/chat.service';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useState } from 'react';

interface UnreadNotification {
  messageId: string;
  conversationId: string;
  senderName: string;
  createdAt: string;
}

interface GroupedNotification {
  conversationId: string;
  senderName: string;
  createdAt: string; // Data da mensagem mais recente
  count: number; // Quantidade de mensagens não lidas
  messageIds: string[]; // IDs de todas as mensagens
}

interface NotificationData {
  hasUnread: boolean;
  messages: UnreadNotification[];
}

// Função para agrupar notificações por conversa
const groupNotificationsByConversation = (
  notifications: UnreadNotification[]
): GroupedNotification[] => {
  const grouped = notifications.reduce(
    (acc, notification) => {
      const { conversationId, messageId, senderName, createdAt } = notification;

      if (!acc[conversationId]) {
        acc[conversationId] = {
          conversationId,
          senderName,
          createdAt,
          count: 0,
          messageIds: [],
        };
      }

      acc[conversationId].count += 1;
      acc[conversationId].messageIds.push(messageId);

      // Manter a data da mensagem mais recente
      if (new Date(createdAt) > new Date(acc[conversationId].createdAt)) {
        acc[conversationId].createdAt = createdAt;
        acc[conversationId].senderName = senderName; // Atualizar com o remetente mais recente
      }

      return acc;
    },
    {} as Record<string, GroupedNotification>
  );

  // Converter para array e ordenar por data mais recente
  return Object.values(grouped).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

export function useNotifications() {
  const { data: session, status } = useSession();
  const [notifications, setNotifications] = useState<NotificationData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (status !== 'authenticated' || !session) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await chatService.getUnreadNotifications(session);
      setNotifications(data);
    } catch (err) {
      setError('Failed to fetch notifications');
    } finally {
      setIsLoading(false);
    }
  }, [session, status]);

  // Fetch notifications on login
  useEffect(() => {
    if (status === 'authenticated') {
      fetchNotifications();
    }
  }, [status, fetchNotifications]);

  // Set up polling every 5 minutes
  useEffect(() => {
    if (status !== 'authenticated') return;

    const interval = setInterval(
      () => {
        fetchNotifications();
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => {
      clearInterval(interval);
    };
  }, [status, fetchNotifications]);

  const markAllAsRead = useCallback(async () => {
    // TODO: Implement API call to mark all as read
    setNotifications((prev) =>
      prev ? { ...prev, hasUnread: false, messages: [] } : null
    );
  }, []);

  // Agrupar notificações por conversa
  const groupedNotifications = notifications?.messages
    ? groupNotificationsByConversation(notifications.messages)
    : [];

  return {
    notifications,
    groupedNotifications,
    isLoading,
    error,
    fetchNotifications,
    markAllAsRead,
    hasUnread: notifications?.hasUnread || false,
    unreadCount: notifications?.messages?.length || 0,
  };
}
